var gigiModalImageCache = window.gigiModalImageCache;
if (!gigiModalImageCache) {
  gigiModalImageCache = new Map();
  window.gigiModalImageCache = gigiModalImageCache;
}

function preloadGigiModalImage(src) {
  if (!src) return Promise.reject();

  if (gigiModalImageCache.has(src)) {
    return gigiModalImageCache.get(src);
  }

  const promise = new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';

    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

  gigiModalImageCache.set(src, promise);
  promise.catch(() => {
    if (gigiModalImageCache.get(src) === promise) gigiModalImageCache.delete(src);
  });
  return promise;
}

var GigiCollectionImageModal = class extends ModalDialog {
  constructor() {
    super();

    this.dialog = this.querySelector('[role="dialog"]');
    this.stage = this.querySelector('[data-gigi-modal-stage]');
    this.frame = this.querySelector('.gigi-collection-image-modal__frame');
    this.loader = this.querySelector('[data-gigi-modal-loader]');
    this.preview = this.querySelector('[data-gigi-modal-preview]');
    this.image = this.querySelector('[data-gigi-modal-image]');
    this.thumbRow = this.querySelector('[data-gigi-modal-thumb-row]');
    this.thumbs = this.querySelector('[data-gigi-modal-thumbs]');
    this.prevButton = this.querySelector('[data-gigi-modal-prev]');
    this.nextButton = this.querySelector('[data-gigi-modal-next]');
    this.mediaKitInfo = this.querySelector('[data-media-kit-modal-info]');
    this.mediaKitTitle = this.querySelector('[data-media-kit-modal-title]');
    this.mediaKitDownload = this.querySelector('[data-media-kit-modal-download]');
    this.images = [];
    this.activeIndex = 0;
    this.pendingSlideIndex = -1;
    this.activeSlideRequest = 0;
    this.activeNavButton = null;
    this.cleanupActiveSlideListeners = null;
    this.handleModalKeydown = this.handleModalKeydown.bind(this);
    this.handleModalKeyup = this.handleModalKeyup.bind(this);

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.hide();
    });

    this.prevButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.go(-1);
    });

    this.nextButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.go(1);
    });

    this.thumbs?.addEventListener('scroll', () => this.updateThumbEdgeState(), {
      passive: true,
    });
  }

  handleModalKeydown(event) {
    if (!this.hasAttribute('open') || event.defaultPrevented) return;

    if (event.code === 'Escape' || event.key === 'Escape') {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;

    const navButton = this.getKeyboardNavButton(event.code);
    if (!navButton) return;

    event.preventDefault();
    this.pressNavButton(navButton);

    if (event.code === 'ArrowLeft') {
      this.go(-1);
      return;
    }

    if (event.code === 'ArrowRight') {
      this.go(1);
    }
  }

  handleModalKeyup(event) {
    if (!this.hasAttribute('open') || event.defaultPrevented) return;

    const navButton = this.getKeyboardNavButton(event.code);
    if (!navButton) return;

    event.preventDefault();
    this.releaseNavButton(navButton);
  }

  getKeyboardNavButton(code) {
    if (code === 'ArrowLeft') {
      return this.prevButton;
    }

    if (code === 'ArrowRight') {
      return this.nextButton;
    }

    return null;
  }

  pressNavButton(button) {
    if (!button || button.hidden || button.disabled || this.images.length <= 1) return;

    if (this.activeNavButton && this.activeNavButton !== button) {
      this.releaseNavButton(this.activeNavButton);
    }

    button.classList.add('is-keyboard-pressed');
    this.activeNavButton = button;
  }

  releaseNavButton(button = this.activeNavButton) {
    if (!button) return;

    button.classList.remove('is-keyboard-pressed');

    if (this.activeNavButton === button) {
      this.activeNavButton = null;
    }
  }

  clearNavButtonPresses() {
    this.releaseNavButton(this.prevButton);
    this.releaseNavButton(this.nextButton);
  }

  animateNavButtonPress(button) {
    if (!button || button.hidden || button.disabled || this.images.length <= 1) return;

    button.classList.add('is-keyboard-pressed');

    window.setTimeout(() => {
      button.classList.remove('is-keyboard-pressed');
    }, 140);
  }

  getPageScrollTop() {
    return (
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  }

  getPageScrollPosition() {
    return {
      x: window.scrollX || window.pageXOffset || 0,
      y: this.getPageScrollTop(),
    };
  }

  getOpenerScrollTop(opener) {
    const savedScroll = opener?.dataset?.gigiPageScrollY;
    if (savedScroll === undefined || savedScroll === '') return null;

    const parsed = Number.parseInt(savedScroll, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  getOpenerScrollPosition(opener) {
    const savedTop = this.getOpenerScrollTop(opener);
    if (savedTop === null) return null;

    return {
      x: window.scrollX || window.pageXOffset || 0,
      y: savedTop,
    };
  }

  show(opener) {
    this.openedBy = opener;
    this.scrollPosition = this.getOpenerScrollPosition(opener) ?? this.getPageScrollPosition();
    this.scrollTop = this.scrollPosition.y;

    const zoomRoot = this.getZoomRoot(opener);
    this.images = this.collectImages(zoomRoot);
    if (!this.images.length) return;

    this.activeIndex = this.getInitialIndex(zoomRoot);
    this.renderThumbs();
    this.updateControls();
    this.scrollActiveThumbIntoView('auto');

    this.lockPageScroll();
    this.openModal(opener);

    this.setSlide(this.activeIndex);
    if (this.dataset.gigiPrefetchMode !== 'adjacent') this.prefetchImages();
  }

  openModal() {
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    window.pauseAllMedia();
    document.addEventListener('keydown', this.handleModalKeydown);
    document.addEventListener('keyup', this.handleModalKeyup);

    const dialog = this.querySelector('[role="dialog"]');
    trapFocus(this, dialog);

    if (dialog) {
      dialog.focus({ preventScroll: true });
    }
  }

  hide() {
    const scrollPosition = this.scrollPosition || {
      x: 0,
      y: this.scrollTop || 0,
    };

    this.activeSlideRequest += 1;
    this.cleanupActiveSlideListeners?.();
    this.cleanupActiveSlideListeners = null;
    this.pendingSlideIndex = -1;

    if (this.loader) {
      this.loader.classList.remove('is-fading-out');
      this.loader.hidden = true;
    }

    this.stage?.classList.remove('is-loading');

    this.closeModal();
    this.unlockPageScroll(scrollPosition);

    this.images = [];
    this.activeIndex = 0;
    this.scrollTop = 0;
    this.scrollPosition = null;

    if (this.thumbs) {
      this.thumbs.innerHTML = '';
      this.thumbs.hidden = true;
      delete this.thumbs.dataset.gigiThumbEdge;
    }

    if (this.thumbRow) {
      this.thumbRow.hidden = true;
    }

    this.updateMediaKitOverlay(null);

    if (this.image) {
      this.image.removeAttribute('src');
      this.image.alt = '';
      this.image.classList.remove('is-visible');
    }

    this.clearFrameSize();
  }

  closeModal() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    document.removeEventListener('keydown', this.handleModalKeydown);
    document.removeEventListener('keyup', this.handleModalKeyup);
    this.clearNavButtonPresses();
    removeTrapFocus(null);

    restoreFocusAfterModal(this.openedBy);

    window.pauseAllMedia();
  }

  getModalMaxHeight() {
    if (!this.image) return window.innerHeight * 0.65;

    const maxHeight = parseFloat(getComputedStyle(this.image).maxHeight);
    return Number.isFinite(maxHeight) ? maxHeight : window.innerHeight * 0.65;
  }

  getModalMaxWidth() {
    return this.stage?.clientWidth || window.innerWidth;
  }

  getModalDisplaySize(width, height) {
    const maxHeight = this.getModalMaxHeight();
    const maxWidth = this.getModalMaxWidth();

    if (!width || !height) {
      return { width: maxWidth, height: maxHeight };
    }

    let displayWidth = width;
    let displayHeight = height;
    const ratio = width / height;

    if (displayHeight > maxHeight) {
      displayHeight = maxHeight;
      displayWidth = displayHeight * ratio;
    }

    if (displayWidth > maxWidth) {
      displayWidth = maxWidth;
      displayHeight = displayWidth / ratio;
    }

    return {
      width: Math.max(1, Math.round(displayWidth)),
      height: Math.max(1, Math.round(displayHeight)),
    };
  }

  applyFrameSize(image) {
    if (!this.frame || !image) return;

    const size = this.getModalDisplaySize(image.width, image.height);
    this.frame.style.width = `${size.width}px`;
    this.frame.style.height = `${size.height}px`;
  }

  clearFrameSize() {
    if (!this.frame) return;

    this.frame.style.width = '';
    this.frame.style.height = '';
  }

  lockPageScroll() {
    this.scrollPosition = this.scrollPosition || this.getPageScrollPosition();
    this.scrollTop = this.scrollPosition.y;

    if (!this.preventBackgroundScroll) {
      this.preventBackgroundScroll = (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.target.closest('gigi-collection-image-modal')) return;
        event.preventDefault();
      };
    }

    document.addEventListener('touchmove', this.preventBackgroundScroll, {
      passive: false,
    });
    document.addEventListener('wheel', this.preventBackgroundScroll, {
      passive: false,
    });
  }

  unlockPageScroll(scrollPosition) {
    const lockedTop = Number.parseInt(document.body.style.top || '0', 10);
    const scrollX = scrollPosition?.x || 0;
    const scrollY =
      Number.isNaN(lockedTop) || lockedTop === 0 ? scrollPosition?.y || 0 : Math.abs(lockedTop);

    document.documentElement.classList.remove('gigi-collection-modal-open');
    document.body.classList.remove('gigi-collection-modal-open');
    document.body.style.top = '';
    document.body.style.width = '';

    document.removeEventListener('touchmove', this.preventBackgroundScroll);
    document.removeEventListener('wheel', this.preventBackgroundScroll);

    window.scrollTo(scrollX, scrollY);
    requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
  }

  getZoomRoot(opener) {
    return (
      opener.closest('[data-media-kit-card]') ||
      opener.closest('[data-gigi-gallery-zoom-root]') ||
      opener.closest('.product-card-wrapper')
    );
  }

  getGfsGalleryRoot(root) {
    if (!root) return null;

    return root.matches('[data-gfs-gallery]') ? root : root.querySelector('[data-gfs-gallery]');
  }

  collectGfsGalleryImages(gallery) {
    const thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');

    if (thumbs.length) {
      return [...thumbs]
        .map((thumb) => ({
          src: thumb.dataset.gigiZoomSrc || thumb.dataset.mediaSrc || '',
          thumbSrc:
            thumb.dataset.thumbSrc ||
            thumb.querySelector('img')?.currentSrc ||
            thumb.querySelector('img')?.src ||
            '',
          alt: thumb.dataset.alt || '',
          width:
            Number(thumb.dataset.width) ||
            Number(thumb.querySelector('img')?.getAttribute('width')) ||
            thumb.querySelector('img')?.naturalWidth ||
            0,
          height:
            Number(thumb.dataset.height) ||
            Number(thumb.querySelector('img')?.getAttribute('height')) ||
            thumb.querySelector('img')?.naturalHeight ||
            0,
        }))
        .filter((image) => image.src);
    }

    const mainImage = gallery.querySelector(
      '.gfs-panel__main-img[data-gigi-zoom-src], .gfs-panel__main-img[data-media-src], .gfs-panel__main-img[src]'
    );

    if (!mainImage || mainImage.classList.contains('placeholder-svg')) return [];

    return [
      {
        src:
          mainImage.dataset.gigiZoomSrc ||
          mainImage.dataset.mediaSrc ||
          mainImage.currentSrc ||
          mainImage.src,
        thumbSrc: mainImage.currentSrc || mainImage.src,
        alt: mainImage.alt || '',
        width: Number(mainImage.getAttribute('width')) || mainImage.naturalWidth || 0,
        height: Number(mainImage.getAttribute('height')) || mainImage.naturalHeight || 0,
      },
    ];
  }

  collectMediaKitImages(root) {
    const kit = root?.closest?.('[data-gigi-media-kit]');
    if (!kit) return [];

    return [...kit.querySelectorAll('[data-media-kit-card]')]
      .filter((card) => !card.hidden && card.dataset.url)
      .map((card) => {
        const image = card.querySelector('img');
        const title = card.dataset.titleDisplay || image?.alt || '';
        const alt = image?.alt || title;
        const filename = card.dataset.downloadName || title || 'gigi-asset';

        return {
          src: card.dataset.url,
          thumbSrc: image?.currentSrc || image?.src || card.dataset.url,
          alt,
          title,
          filename,
          width:
            Number(card.dataset.width) ||
            Number(image?.getAttribute('width')) ||
            image?.naturalWidth ||
            0,
          height:
            Number(card.dataset.height) ||
            Number(image?.getAttribute('height')) ||
            image?.naturalHeight ||
            0,
          mediaKitCard: card,
        };
      });
  }

  collectImages(root) {
    if (!root) return [];

    if (root.matches?.('[data-media-kit-card]')) {
      return this.collectMediaKitImages(root);
    }

    const galleryImages = root.querySelectorAll('[data-gigi-gallery-image]');

    if (galleryImages?.length) {
      return [...galleryImages].map((img) => ({
        src: img.dataset.gigiZoomSrc || img.currentSrc || img.src,
        thumbSrc: img.currentSrc || img.src,
        alt: img.alt || '',
        width: Number(img.getAttribute('width')) || img.naturalWidth || 0,
        height: Number(img.getAttribute('height')) || img.naturalHeight || 0,
      }));
    }

    const gfsGallery = this.getGfsGalleryRoot(root);
    if (gfsGallery) {
      return this.collectGfsGalleryImages(gfsGallery);
    }

    const singleImage =
      root.querySelector('.card__media img[data-gigi-zoom-src]') ||
      root.querySelector('.card__media img');

    if (!singleImage) return [];

    return [
      {
        src: singleImage.dataset.gigiZoomSrc || singleImage.currentSrc || singleImage.src,
        thumbSrc: singleImage.currentSrc || singleImage.src,
        alt: singleImage.alt || '',
        width: Number(singleImage.getAttribute('width')) || singleImage.naturalWidth || 0,
        height: Number(singleImage.getAttribute('height')) || singleImage.naturalHeight || 0,
      },
    ];
  }

  getInitialIndex(root) {
    if (!root) return 0;

    if (root.matches?.('[data-media-kit-card]')) {
      const index = this.images.findIndex((image) => image.mediaKitCard === root);
      return index >= 0 ? index : 0;
    }

    const activeImage = root.querySelector('[data-gigi-gallery-image].is-active');
    if (activeImage) {
      const galleryImages = root.querySelectorAll('[data-gigi-gallery-image]');
      const index = [...galleryImages].indexOf(activeImage);
      return index >= 0 ? index : 0;
    }

    const gfsGallery = this.getGfsGalleryRoot(root);
    if (gfsGallery) {
      const activeThumb = gfsGallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
      if (activeThumb) {
        const thumbs = gfsGallery.querySelectorAll('[data-gfs-gallery-thumb]');
        const index = [...thumbs].indexOf(activeThumb);
        return index >= 0 ? index : 0;
      }
    }

    return 0;
  }

  prefetchImages() {
    const images =
      this.dataset.gigiPrefetchMode === 'adjacent' && this.images.length > 1
        ? [
            this.images[this.activeIndex],
            this.images[(this.activeIndex + 1) % this.images.length],
            this.images[(this.activeIndex - 1 + this.images.length) % this.images.length],
          ]
        : this.images;

    [...new Set(images)].forEach((image) => {
      preloadGigiModalImage(image.src).catch(() => {});
    });
  }

  renderThumbs() {
    if (!this.thumbs) return;

    this.thumbs.innerHTML = '';

    if (this.images.length <= 1) {
      this.thumbs.hidden = true;
      delete this.thumbs.dataset.gigiThumbEdge;
      return;
    }

    this.thumbs.hidden = false;

    this.images.forEach((image, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gigi-collection-image-modal__thumb';
      button.dataset.gigiModalThumb = '';
      button.setAttribute(
        'aria-label',
        image.title || image.alt || image.filename || `Image ${index + 1}`
      );
      button.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');

      const thumbImage = document.createElement('img');
      thumbImage.src = image.thumbSrc;
      thumbImage.alt = '';
      thumbImage.loading = 'lazy';
      thumbImage.className = 'gigi-collection-image-modal__thumb-image';
      button.appendChild(thumbImage);

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setSlide(index);
      });

      this.thumbs.appendChild(button);
    });

    this.updateThumbEdgeState();
  }

  go(delta) {
    if (this.images.length <= 1) return;

    const nextIndex = (this.activeIndex + delta + this.images.length) % this.images.length;
    this.setSlide(nextIndex);
  }

  updateMediaKitOverlay(image) {
    if (!this.mediaKitInfo) return;

    if (!image) {
      this.mediaKitInfo.hidden = true;
      if (this.mediaKitTitle) this.mediaKitTitle.textContent = '';
      if (this.mediaKitDownload) {
        this.mediaKitDownload.removeAttribute('href');
        this.mediaKitDownload.removeAttribute('download');
        delete this.mediaKitDownload.dataset.mediaKitUrl;
        delete this.mediaKitDownload.dataset.mediaKitFilename;
      }
      return;
    }

    const assetTitle = image.title || image.alt || image.filename || 'GiGi media asset';
    const filename = image.filename || assetTitle || 'gigi-asset';

    this.mediaKitInfo.hidden = false;
    if (this.mediaKitTitle) this.mediaKitTitle.textContent = assetTitle;
    if (this.mediaKitDownload) {
      this.mediaKitDownload.href = image.src;
      this.mediaKitDownload.download = filename;
      this.mediaKitDownload.dataset.mediaKitUrl = image.src;
      this.mediaKitDownload.dataset.mediaKitFilename = filename;
    }
  }

  showLoadingState(image) {
    this.stage?.classList.add('is-loading');
    this.applyFrameSize(image);

    const revealLoader = () => {
      if (this.loader) {
        this.loader.hidden = false;
        this.loader.classList.remove('is-fading-out');
      }

      this.image?.classList.remove('is-visible');
    };

    if (this.preview && image.thumbSrc) {
      this.preview.onload = null;
      this.preview.onerror = null;

      if (this.preview.src !== image.thumbSrc) {
        this.preview.src = image.thumbSrc;
      }

      revealLoader();
      return;
    }

    revealLoader();
  }

  revealLoadedImage() {
    if (!this.image) {
      return Promise.resolve();
    }

    const loaderActive =
      this.loader && !this.loader.hidden && !this.loader.classList.contains('is-fading-out');

    if (!loaderActive) {
      this.stage?.classList.remove('is-loading');
      this.image.classList.add('is-visible');
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const finish = () => {
        this.stage?.classList.remove('is-loading');

        if (this.loader) {
          this.loader.classList.remove('is-fading-out');
          this.loader.hidden = true;
        }

        resolve();
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.image.classList.add('is-visible');
          this.loader?.classList.add('is-fading-out');

          let completed = false;
          const complete = () => {
            if (completed) return;
            completed = true;
            finish();
          };

          this.loader?.addEventListener(
            'transitionend',
            (event) => {
              if (event.target === this.loader && event.propertyName === 'opacity') {
                complete();
              }
            },
            { once: true }
          );

          window.setTimeout(complete, 320);
        });
      });
    });
  }

  setSlide(index) {
    const image = this.images[index];
    if (!image || !this.image) return;

    this.cleanupActiveSlideListeners?.();
    this.cleanupActiveSlideListeners = null;
    this.activeIndex = index;

    if (this.dataset.gigiPrefetchMode === 'adjacent') this.prefetchImages();
    this.pendingSlideIndex = index;
    const requestId = ++this.activeSlideRequest;

    this.thumbs?.querySelectorAll('[data-gigi-modal-thumb]').forEach((thumb, thumbIndex) => {
      const isActive = thumbIndex === index;
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');

      if (isActive) {
        this.scrollThumbIntoView(thumb);
      }
    });

    this.updateThumbEdgeState();

    this.image.alt = image.alt;
    this.updateMediaKitOverlay(image);

    const showImmediately = async (loadedImage, source = image.src) => {
      if (requestId !== this.activeSlideRequest) return;

      const sizedImage = loadedImage
        ? {
            ...image,
            width: loadedImage.naturalWidth || image.width,
            height: loadedImage.naturalHeight || image.height,
          }
        : image;

      this.applyFrameSize(sizedImage);
      this.image.src = source;

      try {
        if (this.image.decode) {
          await this.image.decode();
        }
      } catch (_error) {
        /* Ignore decode failures and fall back to the load event path. */
      }

      if (requestId !== this.activeSlideRequest) return;

      await this.revealLoadedImage();
    };

    if (this.image.src === image.src && this.image.complete && this.image.naturalWidth > 0) {
      void showImmediately({
        naturalWidth: this.image.naturalWidth,
        naturalHeight: this.image.naturalHeight,
      });
      return;
    }

    this.showLoadingState(image);

    let completed = false;
    const complete = (loadedImage = null, source = image.src) => {
      if (completed) return;
      if (requestId !== this.activeSlideRequest) {
        cleanupImageListeners();
        return;
      }

      completed = true;
      cleanupImageListeners();
      void showImmediately(loadedImage, source);
    };
    let requestedSource = image.src;
    let triedThumbnail = false;
    const handleLoad = () => {
      complete(
        {
          naturalWidth: this.image.naturalWidth,
          naturalHeight: this.image.naturalHeight,
        },
        requestedSource
      );
    };
    const handleError = () => {
      if (requestId !== this.activeSlideRequest) {
        cleanupImageListeners();
        return;
      }
      if (!triedThumbnail && image.thumbSrc && image.thumbSrc !== image.src) {
        triedThumbnail = true;
        requestedSource = image.thumbSrc;
        this.image.src = requestedSource;
        return;
      }
      cleanupImageListeners();
      this.hide();
    };

    const cleanupImageListeners = () => {
      this.image.removeEventListener('load', handleLoad);
      this.image.removeEventListener('error', handleError);
      if (this.cleanupActiveSlideListeners === cleanupImageListeners) {
        this.cleanupActiveSlideListeners = null;
      }
    };
    this.cleanupActiveSlideListeners = cleanupImageListeners;

    this.image.addEventListener('load', handleLoad);
    this.image.addEventListener('error', handleError);
    this.image.src = image.src;

    if (this.image.complete && this.image.naturalWidth > 0) {
      handleLoad();
      return;
    }

    preloadGigiModalImage(image.src)
      .then((loadedImage) => complete(loadedImage))
      .catch(() => {});
  }

  updateControls() {
    const hasMultipleImages = this.images.length > 1;

    if (this.thumbRow) this.thumbRow.hidden = !hasMultipleImages;
    if (this.prevButton) this.prevButton.hidden = !hasMultipleImages;
    if (this.nextButton) this.nextButton.hidden = !hasMultipleImages;
    this.updateThumbEdgeState();
  }

  scrollActiveThumbIntoView(behavior = 'smooth') {
    const activeThumb = this.thumbs?.querySelector('[data-gigi-modal-thumb][aria-current="true"]');
    this.scrollThumbIntoView(activeThumb, behavior);
  }

  scrollThumbIntoView(thumb, behavior = 'smooth') {
    if (!thumb || !this.thumbs) return;

    const thumbCenter = thumb.offsetLeft + thumb.offsetWidth / 2;
    const maxScrollLeft = Math.max(0, this.thumbs.scrollWidth - this.thumbs.clientWidth);
    const targetScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, thumbCenter - this.thumbs.clientWidth / 2)
    );

    if (behavior === 'auto' || typeof this.thumbs.scrollTo !== 'function') {
      this.thumbs.scrollLeft = targetScrollLeft;
      return;
    }

    if (typeof this.thumbs.scrollTo === 'function') {
      this.thumbs.scrollTo({
        left: targetScrollLeft,
        behavior,
      });
    }
  }

  updateThumbEdgeState() {
    if (!this.thumbs) return;

    const maxScrollLeft = Math.max(0, this.thumbs.scrollWidth - this.thumbs.clientWidth);
    const hasOverflow = maxScrollLeft > 1;

    if (!hasOverflow || this.images.length <= 1) {
      this.thumbs.dataset.gigiThumbEdge = 'none';
      return;
    }

    const scrollLeft = this.thumbs.scrollLeft;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= maxScrollLeft - 1;

    if (atStart && atEnd) {
      this.thumbs.dataset.gigiThumbEdge = 'none';
      return;
    }

    if (atStart) {
      this.thumbs.dataset.gigiThumbEdge = 'end';
      return;
    }

    if (atEnd) {
      this.thumbs.dataset.gigiThumbEdge = 'start';
      return;
    }

    this.thumbs.dataset.gigiThumbEdge = 'both';
  }
};

if (!customElements.get('gigi-collection-image-modal')) {
  customElements.define('gigi-collection-image-modal', GigiCollectionImageModal);
}

function bindGigiCollectionZoomButtons() {
  if (document.body.dataset.gigiCollectionZoomBound === 'true') return;
  document.body.dataset.gigiCollectionZoomBound = 'true';

  const savePageScrollForZoom = (event) => {
    const button = event.target.closest('[data-gigi-card-zoom]');
    if (!button) return;

    button.dataset.gigiPageScrollY = String(
      window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0
    );
  };

  document.addEventListener('pointerdown', savePageScrollForZoom, true);
  document.addEventListener('touchstart', savePageScrollForZoom, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gigi-card-zoom]');
    if (!button) return;

    const modalSelector = button.getAttribute('data-modal');
    const modal = modalSelector ? document.querySelector(modalSelector) : null;
    if (!modal || typeof modal.show !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    modal.show(button);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindGigiCollectionZoomButtons);
} else {
  bindGigiCollectionZoomButtons();
}
